import React, { useEffect, useMemo } from "react";
import { QuickActionTypes } from "./QuickActionsTypes";
import AddOnIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/AddOnIconPath";
import SpeechBubbleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/SpeechBubbleIconPath";
import BoxCancelIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/BoxCancelIconPath";
import ChairIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChairIconPath";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { Trans } from "@coworker/locales";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";
import { TaskTypesRoutes } from "../../constants/taskTypesRoutes";
import trackerHlper from "../../../../../coworker-app/src/helpers/tracker";
import { QuickActionsTitle } from "./QuickActionsTitle";
import "./quick-actions.css";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import { FixaOverflowCarousel } from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import MultiAddOnIconPath from "../../../shared/wrappers/FixaSSRIcon/paths/MultiAddOnIconPath";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";

import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

function buttonTypeTrimmer(buttonType: string) {
  return buttonType.substring(0, buttonType.length - 6);
}

interface QuickActionsProps {
  id: string;
  setOpenProductQualityErrorModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

type QuickAction = {
  id: string;
  text: React.ReactNode;
  icon: (prefix?: string) => React.SVGProps<SVGElement>[];
  onClick: () => void;
  testId: string;
  disabled?: boolean;
};

export const QuickActions: React.FC<QuickActionsProps> = ({
  id,
  setOpenProductQualityErrorModal,
}) => {
  const qualityTeamId = useProductQualityTeamId();
  const navigate = useFixaNavigate();
  const enableMfaqV2 = useFlag(FLAGS.ENABLE_MFAQ_FOLLOWUP_TASK_V2);

  const [, setSearchParams] = useSearchParamState();
  const { data: taskTypes } = useAllTaskTypes();

  useEffect(() => {
    const openNewAddon = new URLSearchParams(window.location.search).get(
      "newTask"
    );
    if (openNewAddon) {
      navigate(`/scanner/${TaskTypesRoutes.ADDON}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { customTaskId, productIssueId, mfaqFollowUpId } = useMemo(() => {
    return {
      customTaskId: taskTypes?.find(
        ({ name }) => name === TaskTypeNames.CUSTOM_TASK
      )?.id,
      productIssueId: taskTypes?.find(
        ({ name }) => name === TaskTypeNames.PRODUCT_ISSUE
      )?.id,
      mfaqFollowUpId: taskTypes?.find(
        ({ name }) => name === TaskTypeNames.MFAQ_FOLLOW_UP
      )?.id,
    };
  }, [taskTypes]);

  const quickActions: QuickAction[] = [
    {
      id: "addon",
      text: <Trans>{QuickActionTypes.ADDON}</Trans>,
      icon: AddOnIconPath,
      onClick: async () => {
        trackerHlper.trackQuickActionsButton(
          buttonTypeTrimmer(QuickActionTypes.ADDON)
        );
        navigate(`/scanner/${TaskTypesRoutes.ADDON}`);
      },
      testId: "quickActionAddon",
    },
    {
      id: "multi-addon",
      text: <Trans>{QuickActionTypes.MULTI_ADDON}</Trans>,
      icon: MultiAddOnIconPath,
      onClick: async () => {
        trackerHlper.trackQuickActionsButton(
          buttonTypeTrimmer(QuickActionTypes.MULTI_ADDON)
        );
        navigate("/multi-order-addons");
      },
      testId: "quickActionMultiAddon",
    },
    {
      id: "mfaq",
      text: QuickActionTypes.MFAQ,
      icon: SpeechBubbleIconPath,
      onClick: () => {
        trackerHlper.trackQuickActionsButton(QuickActionTypes.MFAQ);
        navigate("/customerquestions");
      },
      testId: "quickActionMFAQ",
      disabled: enableMfaqV2 && mfaqFollowUpId === undefined,
    },
    {
      id: "pqr",
      text: <Trans>{QuickActionTypes.PRODUCT_QUALITY}</Trans>,
      icon: BoxCancelIconPath,
      onClick: async () => {
        trackerHlper.trackQuickActionsButton(
          buttonTypeTrimmer(QuickActionTypes.PRODUCT_QUALITY)
        );
        if (!qualityTeamId) {
          setOpenProductQualityErrorModal(true);
          return;
        }
        navigate(`/scanner/${TaskTypesRoutes.PRODUCT_QUALITY}`);
      },
      testId: "quickActionProductQuality",
    },
    {
      id: "product-issue",
      text: <Trans>{QuickActionTypes.PRODUCT_ISSUE}</Trans>,
      icon: ChairIconPath,
      disabled: productIssueId === undefined,
      onClick: async () => {
        trackerHlper.trackQuickActionsButton(
          buttonTypeTrimmer(QuickActionTypes.PRODUCT_ISSUE)
        );

        setSearchParams((prev) => {
          prev.set("taskCreator", String(productIssueId));
        });
      },
      testId: "quickActionProductIssue",
    },
    {
      id: "task",
      text: <Trans>{QuickActionTypes.TASK}</Trans>,
      icon: PencilIconPath,
      disabled: customTaskId === undefined,
      onClick: async () => {
        trackerHlper.trackQuickActionsButton(
          buttonTypeTrimmer(QuickActionTypes.TASK)
        );
        setSearchParams((prev) => {
          prev.set("taskCreator", String(customTaskId));
        });
      },
      testId: "quickActionCustom",
    },
  ].filter(Boolean);

  const quickActionItems = quickActions.map((quickaction) => {
    return (
      <FixaButton
        type="primary"
        key={quickaction.id}
        text={quickaction.text}
        onClick={quickaction.onClick}
        size="small"
        ssrIcon={quickaction.icon}
        className="quick-actions-button"
        data-testid={quickaction.testId}
        disabled={quickaction.disabled}
      />
    );
  });

  return (
    <div>
      <QuickActionsTitle />
      <FixaOverflowCarousel id={id} controlBtnType="primary">
        {quickActionItems.map((item: React.ReactElement, i: number) => {
          return React.cloneElement(item, { key: i });
        })}
      </FixaOverflowCarousel>
    </div>
  );
};
